/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useState } from 'react';
import { 
  FileText, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Clock,
  CheckCircle2,
  XCircle,
  Download,
  Printer,
  ChevronDown
} from 'lucide-react';
import { generateQuotePdf } from '../lib/quotePdf';

import { cn } from '../lib/utils';
import { Toaster } from 'sonner';
import { notify } from '../services/notify';

interface PublicQuotePortalProps {
  quoteId: string;
}

export default function PublicQuotePortal({ quoteId }: PublicQuotePortalProps) {
  const [quote, setQuote] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orgSettings, setOrgSettings] = useState<any>(null);
  const [brandingTheme, setBrandingTheme] = useState<any>(null);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  useEffect(() => {
    if (!quoteId) return;

    let cancelled = false;
    fetch(`/api/public/documents/${encodeURIComponent(quoteId)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'Quote not found' : 'Error fetching quote');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (data.type !== 'quote') {
          setError('Invalid document type');
          setLoading(false);
          return;
        }
        setQuote({
          ...data,
          expiryDate: data.expiryDate || data.dueDate || '',
          items: Array.isArray(data.items) ? data.items : []
        });
        if (data.orgSettings) setOrgSettings(data.orgSettings);
        if (data.brandingThemeData) setBrandingTheme(data.brandingThemeData);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Error fetching quote');
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [quoteId]);

  const handleUpdateStatus = async (newStatus: 'Accepted' | 'Declined', reason?: string) => {
    if (!quote) return;
    setIsUpdatingStatus(true);
    try {
      const res = await fetch(`/api/public/documents/${encodeURIComponent(quote.id)}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, ...(reason ? { declineReason: reason } : {}) }),
      });
      if (!res.ok) throw new Error('Failed to update quote status');
      setQuote((prev: any) => prev ? { ...prev, status: newStatus, ...(reason ? { declineReason: reason } : {}) } : prev);
      setIsDeclineModalOpen(false);
      setDeclineReason('');
      notify.confirm(`Quote ${newStatus} successfully`);
    } catch (err) {
      console.error('Public quote status update failed:', err);
      notify.error('Failed to update quote status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleConfirmDecline = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const reason = declineReason.trim();
    if (!reason) return;
    void handleUpdateStatus('Declined', reason);
  };

  const calculateItemSubtotal = (item: any) => {
    return (item.quantity * item.unitPrice) * (1 - (item.discount || 0) / 100);
  };

  const calculateItemTax = (item: any) => {
    const subtotal = calculateItemSubtotal(item);
    // Rough estimate or pull from settings, but for simple portal we use a standard rate if not specified
    const rate = parseFloat(String(item?.taxRate || '').replace(/[^0-9.]/g, '') || '0') / 100;
    return subtotal * rate;
  };

  const calculateItemTotal = (item: any) => {
    return calculateItemSubtotal(item) + calculateItemTax(item);
  };

  const handleDownloadPDF = async () => {
    if (!quote) return;

    try {
      await generateQuotePdf({
        quote,
        organization: orgSettings,
        calculateSubtotal: calculateItemSubtotal,
        calculateTax: calculateItemTax,
      });
      notify.confirm('PDF downloaded successfully');
    } catch (err) {
      console.error('PDF Generation Error:', err);
      notify.error(`Failed to generate PDF: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E4E3E0] flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-[#141414]/10 border-t-[#141414] rounded-full animate-spin" />
        <p className="font-bold text-[#141414]/40 uppercase tracking-widest text-xs">Loading Quote...</p>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen bg-[#E4E3E0] flex flex-col items-center justify-center p-8 text-center">
        <XCircle className="w-16 h-16 text-red-500 mb-4 opacity-50" />
        <h1 className="text-2xl font-bold text-[#141414] italic serif mb-2">Something went wrong</h1>
        <p className="text-[#141414]/60 max-w-md">{error || 'This quote link might be invalid or expired.'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E4E3E0] text-[#141414] font-sans selection:bg-[#141414] selection:text-[#E4E3E0]">
      <Toaster position="bottom-center" visibleToasts={1} duration={2000} />
      
      {/* Portal Header */}
      <header className="bg-white/50 backdrop-blur-md border-b border-[#141414]/10 sticky top-0 z-50 px-8 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#141414] text-[#E4E3E0] rounded-xl flex items-center justify-center rotate-3 shadow-lg">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Client Portal</p>
              <h1 className="text-lg font-bold italic serif leading-none">Quote {quote.number}</h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button 
              onClick={() => window.print()}
              className="p-2 hover:bg-[#141414]/5 rounded-xl transition-colors text-[#141414]/60 hover:text-[#141414]"
              title="Print Quote"
            >
              <Printer className="w-5 h-5" />
            </button>
            <button 
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-widest hover:opacity-90 transition-all shadow-lg active:scale-95"
            >
              <Download className="w-4 h-4" />
              <span>Download PDF</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-8 md:p-16 space-y-12">
        {/* Quote Status Banner */}
        {quote.status !== 'Draft' && (
          <div className={cn(
            "p-6 rounded-2xl border flex items-center justify-between",
            quote.status === 'Sent' ? "bg-blue-500/10 border-blue-200 text-blue-700" :
            quote.status === 'Accepted' ? "bg-green-500/10 border-green-200 text-green-700" :
            quote.status === 'Declined' ? "bg-red-500/10 border-red-200 text-red-700" :
            "bg-gray-500/10 border-gray-200 text-gray-700"
          )}>
            <div className="flex items-center gap-4">
              {quote.status === 'Accepted' ? <CheckCircle2 className="w-6 h-6" /> : <Clock className="w-6 h-6" />}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest opacity-60">Status</p>
                <p className="font-bold text-lg italic serif">This quote is currently {quote.status.toLowerCase()}.</p>
              </div>
            </div>
            {quote.status === 'Sent' && (
              <div className="flex gap-3">
                <button 
                  onClick={() => setIsDeclineModalOpen(true)}
                  className="px-6 py-2 border-2 border-red-200 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-red-500 hover:text-white hover:border-red-500 transition-all active:scale-95"
                >
                  Decline
                </button>
                <button 
                  onClick={() => void handleUpdateStatus('Accepted')}
                  disabled={isUpdatingStatus}
                  className="px-6 py-2 bg-green-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-green-700 transition-all shadow-lg active:scale-95 disabled:opacity-50"
                >
                  Accept Quote
                </button>
              </div>
            )}
          </div>
        )}

        {isDeclineModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#141414]/60 p-4 backdrop-blur-sm">
            <form onSubmit={handleConfirmDecline} className="w-full max-w-lg rounded-3xl border border-[#141414]/10 bg-white p-8 shadow-2xl">
              <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold italic serif">Decline Quote</h2>
                  <p className="mt-2 text-sm text-[#141414]/60">Please add a reason for declining this quote.</p>
                </div>
                <button type="button" onClick={() => setIsDeclineModalOpen(false)} className="rounded-full p-2 hover:bg-[#141414]/5" aria-label="Cancel">
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
              <label htmlFor="public-decline-reason" className="mb-2 block text-xs font-bold uppercase tracking-widest text-[#141414]/60">Reason for declining *</label>
              <textarea
                id="public-decline-reason"
                autoFocus
                required
                rows={5}
                maxLength={1000}
                value={declineReason}
                onChange={(event) => setDeclineReason(event.target.value)}
                placeholder="Tell us why this quote is being declined..."
                className="w-full resize-none rounded-2xl border border-[#141414]/20 p-4 text-sm outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
              />
              <p className="mt-1 text-right text-[10px] text-[#141414]/40">{declineReason.length}/1000</p>
              <div className="mt-6 flex gap-3">
                <button type="button" onClick={() => setIsDeclineModalOpen(false)} className="flex-1 rounded-xl border border-[#141414]/20 px-5 py-3 text-xs font-bold uppercase tracking-widest hover:bg-[#141414]/5">Cancel</button>
                <button type="submit" disabled={isUpdatingStatus || !declineReason.trim()} className="flex-1 rounded-xl bg-red-600 px-5 py-3 text-xs font-bold uppercase tracking-widest text-white hover:bg-red-700 disabled:opacity-50">{isUpdatingStatus ? 'Saving...' : 'Confirm Decline'}</button>
              </div>
            </form>
          </div>
        )}

        {/* Quote Content Card */}
        <div className="bg-white border border-[#141414]/10 rounded-3xl shadow-2xl overflow-hidden print:border-0 print:shadow-none">
          {/* Company Info */}
          <div className="p-12 bg-white/50 border-b border-[#141414]/5 space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start gap-8">
              <div className="space-y-4">
                {(brandingTheme?.logoOverrideUrl || brandingTheme?.logoUrl || orgSettings?.logoUrl) ? (
                  <img 
                    src={brandingTheme?.logoOverrideUrl || brandingTheme?.logoUrl || orgSettings.logoUrl} 
                    alt="Logo" 
                    className={cn(
                      "object-contain",
                      brandingTheme?.logoSize === 'Small' ? 'h-12' : 
                      brandingTheme?.logoSize === 'Large' ? 'h-32' : 'h-20'
                    )}
                    referrerPolicy="no-referrer" 
                  />
                ) : (
                  <div className="w-16 h-16 bg-[#141414] rounded-2xl flex items-center justify-center font-bold text-2xl text-[#E4E3E0] italic serif">
                    {orgSettings?.name?.substring(0, 1) || 'A'}
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-bold tracking-tight italic serif">{orgSettings?.name || 'Accountex Organization'}</h2>
                  <div className="mt-2 space-y-1 text-xs text-[#141414]/60 font-medium">
                    <p className="flex items-center gap-2">
                      <MapPin className="w-3 h-3" /> 
                      {brandingTheme?.organizationDetails || orgSettings?.address || 'Company Address'}
                    </p>
                    {orgSettings?.website && <p className="flex items-center gap-2"><Globe className="w-3 h-3" /> {orgSettings.website}</p>}
                    {orgSettings?.email && <p className="flex items-center gap-2"><Mail className="w-3 h-3" /> {orgSettings.email}</p>}
                    {orgSettings?.phone && <p className="flex items-center gap-2"><Phone className="w-3 h-3" /> {orgSettings.phone}</p>}
                  </div>
                </div>
              </div>
              <div className="text-right space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#141414]/40">Quotation</p>
                <p className="text-4xl font-bold tracking-tighter italic serif">{quote.number}</p>
                <div className="pt-4 space-y-1">
                  {quote.reference && <p className="text-xs font-bold">Reference: <span className="opacity-60">{quote.reference}</span></p>}
                  <p className="text-xs font-bold">Issue Date: <span className="opacity-60">{new Date(quote.date).toLocaleDateString()}</span></p>
                  <p className="text-xs font-bold">Expiry Date: <span className="opacity-60">{new Date(quote.expiryDate).toLocaleDateString()}</span></p>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-[#141414]/5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#141414]/40 mb-2">Attention</p>
              <p className="text-lg font-bold italic serif">{quote.contactName || 'Valued Customer'}</p>
              <p className="text-xs text-[#141414]/60">{quote.contactEmail}</p>
            </div>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#141414]/5 border-b border-[#141414]/10">
                  <th className="py-4 px-12 text-[10px] font-bold text-[#141414]/50 uppercase tracking-widest">Description</th>
                  <th className="py-4 px-4 text-[10px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">Qty</th>
                  <th className="py-4 px-4 text-[10px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">Unit Price</th>
                  <th className="py-4 px-4 text-[10px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">Disc</th>
                  <th className="py-4 px-12 text-[10px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#141414]/5">
                {quote.items.map((item: any, i: number) => (
                  <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-6 px-12">
                      <p className="text-sm font-bold text-[#141414] italic serif">{item.description}</p>
                    </td>
                    <td className="py-6 px-4 text-sm font-bold text-[#141414] text-right">{item.quantity}</td>
                    <td className="py-6 px-4 text-sm font-bold text-[#141414] text-right">{(item.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="py-6 px-4 text-sm font-bold text-[#141414] text-right">{(item.discount && item.discount > 0) ? ((item as any).discountType === 'amount' ? item.discount.toLocaleString() : `${item.discount}%`) : ''}</td>
                    <td className="py-6 px-12 text-sm font-bold text-[#141414] text-right">
                      {calculateItemTotal(item).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals Section */}
          <div className="p-12 flex flex-col items-end border-t border-[#141414]/10 bg-[#141414]/[0.02]">
            <div className="w-full max-w-xs space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[#141414]/60">Subtotal</span>
                <span className="text-[#141414]">{quote.currency} {quote.items.reduce((sum: number, item: any) => sum + calculateItemSubtotal(item), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#141414]/60">Tax</span>
                <span className="text-[#141414]">{quote.currency} {quote.items.reduce((sum: number, item: any) => sum + calculateItemTax(item), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="pt-6 mt-6 border-t border-[#141414]/10 flex justify-between items-baseline">
                <span className="text-sm font-bold uppercase tracking-wider text-[#141414]/60">Total</span>
                <span className="text-3xl font-bold text-[#141414] tracking-tighter">{quote.currency} {(quote.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          {/* Footer / Terms */}
          {(quote.termsAndConditions || brandingTheme?.invoiceFooterText) && (
            <div className="p-12 pt-0 border-t border-[#141414]/5 bg-gray-50/30">
              <div className="max-w-xl space-y-8">
                {quote.termsAndConditions && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#141414]/40 mb-2">Terms & Conditions</p>
                    <p className="text-xs text-[#141414]/70 leading-relaxed italic serif whitespace-pre-wrap">{quote.termsAndConditions}</p>
                  </div>
                )}
                {brandingTheme?.invoiceFooterText && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#141414]/40 mb-2">Notes</p>
                    <p className="text-xs text-[#141414]/70 leading-relaxed italic serif whitespace-pre-wrap">{brandingTheme.invoiceFooterText}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <footer className="text-center space-y-4 opacity-40 py-12">
          <p className="text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2">
            Powered by Accountex Business Intelligence
          </p>
          <div className="flex justify-center gap-4 text-[10px]">
            <a href="#" className="hover:underline">Privacy Policy</a>
            <span>•</span>
            <a href="#" className="hover:underline">Terms of Service</a>
            <span>•</span>
            <a href="#" className="hover:underline">Contact Support</a>
          </div>
        </footer>
      </main>
    </div>
  );
}
